/**
 * Knowledge Base · one General Knowledge reference file
 * (/knowledge/buckets/:bucketId/documents/:documentId): what it is and the
 * equipment it applies to. Untagged files apply everywhere; tagged ones only
 * to questions about that equipment.
 */
import { useParams } from 'react-router-dom';
import { Badge, Button, Modal, Skeleton } from '@servicecompressionllc/sc-design-system';
import { Breadcrumbs } from '../../components/Breadcrumbs.js';
import { formatDate, humanSize } from '../../lib/format.js';
import { SubjectPickerModal } from './SubjectPickerModal.js';
import { useDocumentPageLogic } from './useDocumentPageLogic.js';

const Fact = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">{label}</span>
    <span className="text-sm text-ink">{value}</span>
  </div>
);

export const DocumentPage = () => {
  const { bucketId = '', documentId = '' } = useParams();
  const {
    doc,
    subjects,
    bucket,
    canWrite,
    picking,
    draft,
    setDraft,
    openPicker,
    closePicker,
    save,
    remove,
    confirmDelete,
    setConfirmDelete,
  } = useDocumentPageLogic(bucketId, documentId);

  if (doc.isLoading) return <Skeleton className="h-64" />;
  if (!doc.data) {
    return (
      <p className="text-sm text-ink-muted">
        {doc.error ? (doc.error as Error).message : 'That file isn’t here.'}
      </p>
    );
  }
  const d = doc.data;
  const bucketName = bucket?.name ?? 'General Knowledge';

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <section>
        <Breadcrumbs
          trail={[
            { label: 'General Knowledge', to: '/knowledge/rules' },
            { label: bucketName, to: `/knowledge/buckets/${bucketId}` },
          ]}
        />
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="min-w-0 break-words text-2xl font-bold text-ink">{d.filename}</h2>
          <Badge variant="neutral">reference file</Badge>
          {canWrite && (
            <span className="ml-auto flex gap-2">
              <Button variant="ghost" onClick={() => setConfirmDelete(true)}>
                Delete
              </Button>
            </span>
          )}
        </div>
      </section>

      <section
        aria-labelledby="applies-to"
        className="flex flex-col gap-4 rounded-lg border border-outline bg-surface-elevated p-5"
      >
        <div className="flex flex-wrap items-start gap-4">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <h3 id="applies-to" className="text-base font-semibold text-ink">
              Applies to
            </h3>
            <p className="text-sm text-ink-muted">
              The equipment this file is about. The assistant only quotes it on questions about that
              equipment. Everywhere means every question, for any equipment.
            </p>
          </div>
          {canWrite && (
            <Button onClick={openPicker} disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Choose equipment'}
            </Button>
          )}
        </div>
        <ul className="flex flex-wrap gap-2" aria-label="Applies to">
          {d.subjects.length === 0 ? (
            <li className="rounded-md border border-outline-strong bg-surface px-3 py-2 text-sm font-medium text-ink">
              Everywhere
            </li>
          ) : (
            d.subjects.map((s) => (
              <li
                key={s.id}
                className="rounded-md border border-outline-strong bg-surface px-3 py-2 text-sm font-medium text-ink"
              >
                {s.name}
                {s.archived && <span className="font-normal text-ink-subtle"> (deleted)</span>}
              </li>
            ))
          )}
        </ul>
        {save.error && <p className="text-sm text-danger">{(save.error as Error).message}</p>}
      </section>

      <section className="grid grid-cols-1 gap-4 rounded-lg border border-outline bg-surface-elevated p-5 sm:grid-cols-2 lg:grid-cols-4">
        <Fact label="Bucket" value={bucketName} />
        <Fact label="Size" value={humanSize(d.sizeBytes)} />
        <Fact label="Uploaded" value={`${formatDate(d.createdAt)} by ${d.uploadedByEmail}`} />
        <Fact label="In chat" value="Cited by filename" />
      </section>

      <SubjectPickerModal
        open={picking}
        subjects={subjects.data ?? []}
        selected={draft}
        onChange={setDraft}
        onClose={closePicker}
      />
      <Modal open={confirmDelete} onOpenChange={setConfirmDelete}>
        <Modal.Header>
          <Modal.Title>Delete this file?</Modal.Title>
          <Modal.CloseButton />
        </Modal.Header>
        <Modal.Body>
          <p className="text-sm text-ink-muted">
            The assistant stops using it straight away, and this can’t be undone.
          </p>
          {remove.error && (
            <p className="mt-3 text-sm text-danger">{(remove.error as Error).message}</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
            Keep it
          </Button>
          <Button variant="danger" onClick={() => remove.mutate()} disabled={remove.isPending}>
            {remove.isPending ? 'Deleting…' : 'Delete file'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
